import { forwardRef, useImperativeHandle, useRef, type PointerEvent } from 'react';

export const MAX_SIGNATURE_BYTES = 512 * 1024;

export type SignatureResult =
  | { kind: 'ok'; dataUrl: string }
  | { kind: 'blank' }
  | { kind: 'too_large' };

export interface SignaturePadHandle {
  read: () => SignatureResult;
  clear: () => void;
}

type Point = { x: number; y: number };

const FALLBACK_EMPTY_PNG = 'data:image/png;base64,iVBORw0KGgo=';

function byteSize(dataUrl: string): number {
  const [, base64 = ''] = dataUrl.split(',');
  return atob(base64).length;
}

export const SignaturePad = forwardRef<SignaturePadHandle, { ariaLabel: string }>(
  function SignaturePad({ ariaLabel }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const dirtyRef = useRef(false);
    const lastPointRef = useRef<Point | null>(null);

    function pointFrom(event: PointerEvent<HTMLCanvasElement>) {
      const rect = event.currentTarget.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    }

    function drawLine(from: Point, to: Point) {
      const context = canvasRef.current?.getContext('2d');
      if (!context) return;
      context.strokeStyle = '#111827';
      context.lineWidth = 2;
      context.lineCap = 'round';
      context.beginPath();
      context.moveTo(from.x, from.y);
      context.lineTo(to.x, to.y);
      context.stroke();
    }

    function startDrawing(event: PointerEvent<HTMLCanvasElement>) {
      drawingRef.current = true;
      dirtyRef.current = true;
      const point = pointFrom(event);
      lastPointRef.current = point;
      drawLine(point, point);
    }

    function continueDrawing(event: PointerEvent<HTMLCanvasElement>) {
      if (!drawingRef.current || lastPointRef.current == null) return;
      const next = pointFrom(event);
      drawLine(lastPointRef.current, next);
      lastPointRef.current = next;
    }

    function stopDrawing() {
      drawingRef.current = false;
      lastPointRef.current = null;
    }

    useImperativeHandle(ref, () => ({
      read() {
        if (!dirtyRef.current) return { kind: 'blank' };
        const dataUrl = canvasRef.current?.toDataURL('image/png') ?? FALLBACK_EMPTY_PNG;
        if (byteSize(dataUrl) > MAX_SIGNATURE_BYTES) return { kind: 'too_large' };
        return { kind: 'ok', dataUrl };
      },
      clear() {
        const canvas = canvasRef.current;
        const context = canvas?.getContext('2d');
        if (canvas && context) {
          context.clearRect(0, 0, canvas.width, canvas.height);
        }
        dirtyRef.current = false;
        stopDrawing();
      },
    }));

    return (
      <div className="w-fit rounded-md border bg-white">
        <canvas
          ref={canvasRef}
          width={320}
          height={160}
          aria-label={ariaLabel}
          className="touch-none"
          onPointerDown={startDrawing}
          onPointerMove={continueDrawing}
          onPointerUp={stopDrawing}
          onPointerLeave={stopDrawing}
        />
      </div>
    );
  },
);
